import React, { useState, useMemo, FunctionComponent, useCallback, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';

import { useConfig } from '@browsing/config';
import { screenSizes } from '@lux/tokens';
import { useWindowWidth } from '@lux/hooks';
import { ExpiredOverlay, CartStatusEnum } from '@pegasus/cart';
import { CommercialMessage } from '@pegasus/commercial-messages';
import { LanguageCode, TranslateScope, useTranslationContext } from '@pegasus/translation';
import { Header, GuestHeader, navigationItem } from '@pegasus/ui-headers';
import { useIsEligibleForVoucher } from '@pegasus/platform-journeys';
import { isSmartBannerClosed } from '@pegasus/smartbanner';
import { useConsentManager } from '@pegasus/tracker';
import { isClient, getSessionStorageItem } from '@pegasus/utils';

import { isMoreThanDaysAgo, isUpcomingEnabled } from '@client/helpers';
import { useZalandoPlus } from '@client/components/mylounge/plus';
import { useCart } from '@client/lib/cart/hooks';
import { getWebviewOs } from '@client/redux/modules/clientDevice/selectors';
import { getToken } from '@client/redux/modules/user/selectors';
import {
    getHeaderNotificationBanner,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import {
    getEmail,
    getFirstName,
    getAreCredentialsCompromised,
    getCustomerAcr,
} from '@client/redux/modules/customer';
import { UspSlider } from '@client/components/mylounge/usp/uspSlidder';
import { trackEvents, M1_REBRANDING } from '@client/components/mylounge/constants';
import useCustomCommercialMessage from '@client/components/mylounge/banner/use-commercial-message';
import { track } from '@client/helpers/tracking';
import { getCookie, setCookie, ONE_DAY } from '@client/helpers/cookie';
import SmartBanner from '@client/components/shared/SmartBanner';
import { USP_VISIBILITY_START_DATE } from '@client/components/constants';
import { SecurityBanner } from '@client/components/shared/SecurityBanner';
import { useBrazeContext } from '@pegasus/braze';
import { useSubscribeToNewsletter, useIsPlatformJourney } from '../mylounge/platform-journeys';
import { getSupportedLanguages, getCredentialsStatus, getTopPosition } from './helpers';
import { Timer } from './timer';
import FlashMessages from './flash-messages';
import Notifications from './notifications';
import { TabsWrapper } from '../mylounge/banner/Tabs/TabsWrapper';
import {
    HeaderWrap,
    TabsContainer,
    HeaderRowsHolder,
    HeaderBannerRow,
} from './headerWrapper.styles';
import { BrazeBanner } from '../brazeCommunication/brazeBanner/BrazeBanner';

type Props = {
    isGuest?: boolean;
    updateApplicationContext?: (isSmartbannerShown: boolean) => void;
};

const LoadableDatePicker = loadable(
    () => import(/* webpackChunkName: "date-picker" */ './date-picker'),
);

export const HeaderWrapper: FunctionComponent<Props> = ({
    isGuest = false,
    updateApplicationContext,
}) => {
    const pathName = window.location.pathname;
    const dispatch = useDispatch();
    const config = useConfig();
    const email = useSelector(getEmail);
    const firstName = useSelector(getFirstName);
    const isApmCommunicationEnabled = config.isEnabled('isAPM');
    const hasCompromisedCredentials = useSelector(getAreCredentialsCompromised);
    const customerAcr = useSelector(getCustomerAcr);
    const isPlatformJourneyEnabled = useIsPlatformJourney();
    const isEligibleForVoucher = useIsEligibleForVoucher();
    const isWebview = !!useSelector(getWebviewOs);
    const showTabsAndCommercialMsg = pathName.startsWith('/event');
    const isDoi = config.isEnabled('doi');
    const { translations } = useTranslationContext();
    const [timerState, setTimerState] = useState<CartStatusEnum>(null);
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);
    const commercialMessage = useCustomCommercialMessage();

    const [hasConsent] = useConsentManager('smart_app_download_banner');
    const [isBannerShown, setIsBannerShown] = useState(true);
    const [isBannerShouldBeRemovedInDom, setIsBannerShouldBeRemovedInDom] = useState(() => {
        return isSmartBannerClosed('mylounge');
    });
    // TODO: check if we need isConsentGiven for staging
    let isConsentGiven = hasConsent;
    isConsentGiven = isConsentGiven || !!getCookie('smartbanner_consent');

    const screenWidth = useWindowWidth();
    const isMobileView = screenWidth < screenSizes.sm;

    const [{ cart, recoveredCart }, cartActions] = useCart();
    const cartInfo = cart && {
        savings: cart.savings,
        total: cart.total,
        shippingCosts: cart.shippingCosts,
        discountPercent: cart.discountPercent,
    };
    const cartItems = (cart && cart.items) || [];
    const recoveredItems = (recoveredCart && recoveredCart.items) || [];
    const csrfToken = useSelector(getToken);
    const headerNotificationBanner = useSelector(getHeaderNotificationBanner);
    const subscribeToNewsletter = useSubscribeToNewsletter();

    const { isEnabled: isPlusEnabled } = useZalandoPlus();
    const supportedLanguages = useMemo(() => getSupportedLanguages(config), [config]);

    const extendCart = useCallback(() => {
        cartActions.onExtendCart();
        setTimerState(null);
    }, [cartActions]);

    const renderTimer = useCallback(
        () => (
            <TranslateScope path="pushNotification">
                <Timer
                    setTimerState={setTimerState}
                    onExpiredCart={cartActions.onExpiredCart}
                    cart={cart}
                />
            </TranslateScope>
        ),
        [cart, cartActions.onExpiredCart],
    );

    const handleComponentRemovalState = useCallback(() => {
        setIsBannerShown(false);
        setIsBannerShouldBeRemovedInDom(true);
    }, [setIsBannerShouldBeRemovedInDom, setIsBannerShown]);

    const handleListenToBannerPosition = useCallback((isShown: boolean) => {
        setIsBannerShown(isShown);
    }, []);

    const [showUSPVisibility] = useState(() =>
        isMoreThanDaysAgo(window.localStorage.getItem(USP_VISIBILITY_START_DATE)),
    );

    useEffect(() => {
        if (updateApplicationContext) {
            updateApplicationContext(!isBannerShouldBeRemovedInDom && isBannerShown);
        }
    }, [updateApplicationContext, isBannerShouldBeRemovedInDom, isBannerShown]);

    const securityBannerViewedCookie = getCookie('securityBannerViewed');
    const [showSecurityBanner, setShowSecurityBanner] = useState(false);

    useEffect(() => {
        if (!securityBannerViewedCookie) {
            setShowSecurityBanner(true);
        } else {
            setShowSecurityBanner(false);
        }
    }, [securityBannerViewedCookie, showSecurityBanner]);

    const handleVisibilityOnClick = () => {
        setShowSecurityBanner(!showSecurityBanner);
        setCookie('securityBannerViewed', 'true', ONE_DAY);
    };

    const isSecurityBanner =
        showSecurityBanner && isApmCommunicationEnabled && hasCompromisedCredentials;

    useEffect(() => {
        const status = getCredentialsStatus(hasCompromisedCredentials, customerAcr);

        track('credential_status_check', { crendentialStatus: status });
    }, [hasCompromisedCredentials, customerAcr]);

    const { cardMethodsFromBraze } = useBrazeContext();
    const [areBrazeMethodsAvailable, setAreBrazeMethodsAvailable] = useState(false);
    useEffect(() => {
        if (cardMethodsFromBraze) {
            setAreBrazeMethodsAvailable(true);
        }
    }, [cardMethodsFromBraze]);

    const isCartItemAndBrazeVoucher =
        cartItems.length > 0 && window.location.search.includes('&brazeVoucher=true');
    const isBrazeVoucherExperience = isCartItemAndBrazeVoucher ? navigationItem.cart : null;
    const isBrazeSnackbar = isCartItemAndBrazeVoucher || false;

    if (isWebview) {
        return null;
    }

    const showBannerAfterDismissal =
        isClient() && getSessionStorageItem('show_braze_voucher_dismissed');
    const cardFromSessionStorageString = isClient() && getSessionStorageItem('brazeCardToRender');
    const cardFromSessionStorage =
        cardFromSessionStorageString && JSON.parse(cardFromSessionStorageString);

    const bannerAllowedByCookie = getCookie('show_braze_banner');
    const canBrazeBannerBeDisplayed =
        showBannerAfterDismissal &&
        bannerAllowedByCookie &&
        cardFromSessionStorage &&
        areBrazeMethodsAvailable;

    const VoucherBanner = canBrazeBannerBeDisplayed && (
        <BrazeBanner
            cardToRender={cardFromSessionStorage}
            cardMethodsFromBraze={cardMethodsFromBraze}
        />
    );

    return (
        <HeaderWrap
            movePostionTopBy={getTopPosition(
                isM1Rebranding,
                showUSPVisibility,
                isBannerShouldBeRemovedInDom,
                isBannerShown,
            )}
        >
            <HeaderRowsHolder>
                {!isBannerShouldBeRemovedInDom && (
                    <HeaderBannerRow>
                        <SmartBanner
                            listenToBannerPosition={handleListenToBannerPosition}
                            updateComponentRemovalState={handleComponentRemovalState}
                        />
                    </HeaderBannerRow>
                )}
                <UspSlider isM1Rebranding={isM1Rebranding} showUSPVisibility={showUSPVisibility} />

                <HeaderBannerRow height={isMobileView ? 60 : 75}>
                    {isGuest ? (
                        <GuestHeader
                            supportedLanguages={supportedLanguages}
                            isPrive={config.isPriveDomain}
                        />
                    ) : (
                        <Header
                            isAutoLoginEnabled={config.isEnabled('autoLoginTooltip')}
                            translations={translations}
                            languageCode={translations.locale as LanguageCode}
                            csrfToken={csrfToken}
                            timer={renderTimer}
                            cart={{
                                items: cartItems,
                                recoveredItems,
                                info: cartInfo,
                                appendComponent: VoucherBanner,
                            }}
                            cartEvents={{
                                onItemRemove: cartActions.onItemRemove,
                                onItemReserveAgain: cartActions.onItemReserveAgain,
                            }}
                            datePicker={isUpcomingEnabled(config) ? <LoadableDatePicker /> : null}
                            supportedLanguages={supportedLanguages}
                            isPlusEnabled={isPlusEnabled}
                            isPrive={config.isPriveDomain}
                            isPlatformJourneyEnabled={isPlatformJourneyEnabled}
                            showSuccessMessage={(message: string) =>
                                dispatch(showHeaderSuccessFlashMessage(message))
                            }
                            isDOIUser={isDoi}
                            isSubscribedToNewsletter={!isEligibleForVoucher}
                            isNwlDialogEnabled
                            subscribeToNewsletter={subscribeToNewsletter}
                            email={email}
                            notification={headerNotificationBanner}
                            firstName={firstName}
                            openOnMount={isBrazeVoucherExperience}
                        />
                    )}
                </HeaderBannerRow>

                {!isM1Rebranding && (
                    <>
                        {isBrazeSnackbar && (
                            <FlashMessages hasSmartBanner={!isBannerShouldBeRemovedInDom} />
                        )}
                        <HeaderBannerRow height="auto">
                            {!isBrazeSnackbar && <FlashMessages />}
                            <Notifications />
                        </HeaderBannerRow>
                    </>
                )}
            </HeaderRowsHolder>

            {isSecurityBanner && (
                <SecurityBanner
                    handleVisibilityOnClick={handleVisibilityOnClick}
                    userEmail={email}
                />
            )}

            {!isSecurityBanner && isM1Rebranding && showTabsAndCommercialMsg && (
                <TranslateScope path="myLounge">
                    {isBrazeSnackbar && (
                        <FlashMessages hasSmartBanner={!isBannerShouldBeRemovedInDom} />
                    )}
                    <TabsContainer isBrazeSnackbar={isBrazeSnackbar}>
                        {!isBrazeSnackbar && <FlashMessages />}
                        <Notifications />
                        <TabsWrapper />
                        {!isPlatformJourneyEnabled &&
                            commercialMessage?.isEnabled &&
                            !commercialMessage?.isModal && (
                                <TransitionGroup className="transition__group">
                                    {commercialMessage.isShown && (
                                        <CSSTransition
                                            classNames="commercial-transition"
                                            timeout={300}
                                        >
                                            <CommercialMessage
                                                event={commercialMessage.event}
                                                onRedeem={commercialMessage.onRedeem}
                                                onClose={commercialMessage.onClose}
                                                track={track}
                                                trackEvents={trackEvents}
                                            />
                                        </CSSTransition>
                                    )}
                                </TransitionGroup>
                            )}
                    </TabsContainer>
                </TranslateScope>
            )}

            <ExpiredOverlay
                translations={translations}
                isShown={!!timerState}
                status={timerState}
                closeDialog={() => {
                    setTimerState(null);
                }}
                extendCart={extendCart}
            />
        </HeaderWrap>
    );
};

export default HeaderWrapper;
